// Dedicated content for the Strategy & Commercial Transformation capability page.
// Rendered by pages/StrategyPage.jsx at /capabilities/strategy-commercial-transformation

export const STRATEGY_PAGE = {
  slug: "strategy-commercial-transformation",
  title: "Strategy & Commercial Transformation",
  metaTitle: "Strategy & Commercial Transformation Consulting | BLEZZINGS",
  metaDescription:
    "Growth and commercial transformation consulting — growth strategy, product portfolio, pricing, go-to-market, route-to-market, sales transformation.",
  heroSubhead: "Turn growth ambition into commercial performance.",
  heroParas: [
    "Growth rarely stalls because businesses lack ambition.",
    "It stalls when strategic choices are unclear, commercial levers work in isolation, or execution cannot keep pace with the strategy.",
    "BLEZZINGS helps businesses identify where profitable growth will come from — and build the commercial system required to capture it.",
    "From growth strategy and product portfolio to pricing, go-to-market, channels, sales and commercial excellence, we connect strategic choices with the capabilities and execution required to deliver measurable revenue, margin and market-share improvement.",
  ],
  heroTagline: "Strategy that makes choices. Transformation that makes them happen.",
  heroCta: "Discuss Your Growth Challenge",

  challengeLead: "Growth gets harder as businesses become more complex.",
  challengeParas: [
    "Early growth can come from a strong product, an attractive market, entrepreneurial energy or a few exceptional people.",
    "But as businesses scale, growth becomes a system problem.",
    "Management begins confronting questions such as:",
  ],
  challengeQuestions: [
    "Where will our next phase of growth come from?",
    "Which markets and categories should we prioritise?",
    "Are we investing behind the right products?",
    "Are we capturing enough of the value we create?",
    "How should we enter new markets?",
    "Which channels should we build?",
    "Is our distribution model still fit for purpose?",
    "Is the sales organisation productive enough?",
    "Why does revenue growth not translate into margin growth?",
    "Are commercial teams aligned around the same priorities?",
    "Do we have the processes and management discipline required to scale?",
  ],
  challengeChainLead: "These questions are interconnected.",
  challengeChain: [
    "A portfolio decision changes pricing.",
    "Pricing affects channels.",
    "Channels affect the sales model.",
    "The sales model affects economics.",
  ],
  challengeClosing:
    "And the strategy only creates value when the entire commercial system can execute it.",

  perspectiveLead:
    "Commercial transformation is not one initiative. It is a connected set of choices.",
  perspectiveIntro:
    "Businesses often try to solve growth problems one function at a time.",
  perspectiveInterventions: [
    "Restructure sales",
    "Change distributor margins",
    "Launch more products",
    "Increase promotions",
    "Enter another market",
    "Run another campaign",
  ],
  perspectiveMid:
    "But isolated interventions rarely fix an interconnected commercial system.",
  perspectiveQuestionsLead: "We look at growth through two fundamental questions:",
  perspectiveQuestions: [
    {
      q: "Where should we play?",
      a: "Which markets, customers, categories, products and opportunities deserve investment?",
    },
    {
      q: "How should we win?",
      a: "What pricing, go-to-market, channel, sales and operating model will allow the business to capture that opportunity profitably?",
    },
  ],
  perspectiveLogicLead: "That creates a connected commercial logic:",
  perspectiveHighlight:
    "Growth Strategy → Product Portfolio → Pricing → Go-To-Market → Route-to-Market → Sales → Commercial Excellence",
  perspectiveClosing: "Every decision should reinforce the next.",

  offersLead: "From strategic choices to commercial execution.",
  offerGroups: [
    {
      label: "Strategic Choices",
      offers: [
        {
          slug: "growth-strategy",
          name: "Growth Strategy",
          lead: "Decide where the next phase of profitable growth will come from.",
          body: "Growth strategy is fundamentally about choice. We help leadership teams identify the markets, customers, categories and growth opportunities worth pursuing — and concentrate investment behind the opportunities with the greatest potential to create sustainable commercial value.",
          list: [
            "Growth diagnostics",
            "Growth-pool identification",
            "Strategic opportunity assessment",
            "Core-business growth",
            "Customer growth opportunities",
            "Category expansion",
            "Geographic expansion",
            "Adjacency strategy",
            "New growth engines",
            "Business-model opportunities",
            "Revenue and margin improvement opportunities",
            "Strategic priorities",
            "Growth scenarios",
            "Value-creation agenda",
            "Strategic roadmap",
          ],
          closing:
            "The objective is not simply to produce a growth target. It is to answer: Where will the growth come from — and what choices must we make to capture it?",
        },
        {
          slug: "product-portfolio-strategy",
          name: "Product Portfolio Strategy",
          lead: "Decide what to sell, where to invest — and what no longer deserves resources.",
          body: "More products do not automatically create more growth. As portfolios expand, complexity increases, investment gets fragmented and products begin competing with one another rather than with the market. BLEZZINGS helps businesses build portfolios in which every category, product and SKU has a clear strategic and commercial role.",
          list: [
            "Portfolio diagnostics",
            "Category strategy",
            "Portfolio architecture",
            "Product roles",
            "SKU performance",
            "SKU rationalisation",
            "Good–Better–Best structures",
            "Product and price ladders",
            "Portfolio gaps",
            "White-space opportunities",
            "Cannibalisation analysis",
            "Innovation priorities",
            "New-product pipelines",
            "Product lifecycle strategy",
            "Portfolio investment prioritisation",
          ],
          closing:
            "The objective is to determine: What should we grow, launch, fix, maintain — or exit?",
        },
        {
          slug: "pricing-revenue-management",
          name: "Pricing & Revenue Management",
          lead: "Capture more of the value you create.",
          body: "Pricing is one of the fastest and most powerful ways to improve profitability — and one of the easiest commercial levers to get wrong. BLEZZINGS helps businesses build pricing and revenue-management systems that balance customer value, competitive position, demand, channel economics and margin.",
          list: [
            "Pricing strategy",
            "Value-based pricing",
            "Price architecture",
            "Willingness-to-pay assessment",
            "Competitive pricing",
            "Price-pack architecture",
            "Channel pricing",
            "Price realisation",
            "Price increases",
            "Discount governance",
            "Promotions",
            "Promotional effectiveness",
            "Trade schemes",
            "Trade investment",
            "Monetisation strategy",
            "Revenue and margin optimisation",
          ],
          closing:
            "The objective is not simply to determine: “What should we charge?” It is to determine: “How do we capture more of the value we create without compromising sustainable growth?”",
        },
      ],
    },
    {
      label: "Commercial Transformation",
      offers: [
        {
          slug: "go-to-market-market-entry",
          name: "Go-To-Market & Market Entry",
          lead: "Turn market opportunity into revenue.",
          body: "A strong product and attractive market do not guarantee a successful launch. Winning requires the right customer, proposition, price, channel, sales motion and demand-generation engine working together. BLEZZINGS designs integrated go-to-market strategies for new products, categories, geographies and business opportunities.",
          list: [
            "Market-entry strategy",
            "Customer and segment prioritisation",
            "Ideal Customer Profile",
            "Customer proposition",
            "Offer and launch strategy",
            "Pricing implications",
            "Channel strategy",
            "Sales motion",
            "Demand-generation strategy",
            "Product launches",
            "Category entry",
            "Geographic expansion",
            "New-business GTM",
            "GTM operating model",
            "Launch roadmap",
            "GTM measurement",
          ],
          closing:
            "The objective is to answer: Who must we win, why should they choose us, and what commercial system will turn the opportunity into revenue?",
        },
        {
          slug: "channel-route-to-market",
          name: "Channel & Route-to-Market",
          lead: "Reach the right customers through the right channels — at the right economics.",
          body: "The channel model that created yesterday's growth may not be the one required for tomorrow. BLEZZINGS helps businesses design route-to-market systems that balance reach, control, customer access, partner economics and cost-to-serve.",
          list: [
            "Route-to-market strategy",
            "Direct vs indirect models",
            "Distributor strategy",
            "Dealer and retailer models",
            "Modern trade",
            "Institutional and B2B channels",
            "Channel partner strategy",
            "Geographic coverage",
            "Territory design",
            "Distributor economics",
            "Partner margins",
            "Channel productivity",
            "Distributor selection",
            "Coverage models",
            "Omnichannel strategy",
            "Offline, marketplace and D2C roles",
            "Channel conflict",
            "Expansion models",
          ],
          closing:
            "The objective is not to add more channels. It is to build the right route to the right customer at sustainable economics.",
        },
        {
          slug: "sales-transformation",
          name: "Sales Transformation",
          lead: "Build a sales engine that can scale beyond individual heroics.",
          body: "Sustainable sales performance requires more than talented salespeople. It requires the right customer coverage, structure, processes, incentives, capabilities and management discipline working as one system. BLEZZINGS helps businesses make sales performance more productive, predictable and repeatable.",
          list: [
            "Sales strategy",
            "Sales organisation design",
            "Roles and responsibilities",
            "Territory design",
            "Sales capacity",
            "Customer and account segmentation",
            "Coverage models",
            "Key Account Management",
            "Sales process",
            "Lead management",
            "Pipeline management",
            "CRM requirements",
            "Sales productivity",
            "Incentive design",
            "Target setting",
            "Forecasting",
            "Sales capability building",
            "Manager coaching",
            "Performance management",
          ],
          closing:
            "The objective is not simply to add more salespeople. It is to create a sales system capable of producing more from the resources already deployed — and scaling effectively as the business grows.",
        },
        {
          slug: "commercial-excellence-operating-model",
          name: "Commercial Excellence & Operating Model",
          lead: "Make commercial performance systematic, measurable and repeatable.",
          body: "Strategy can be right and still fail. Pricing can be redesigned. New channels can be opened. Sales teams can be restructured. But without clear accountability, management routines, analytics and cross-functional discipline, improvements often disappear over time. BLEZZINGS helps institutionalise the commercial capabilities required to sustain performance.",
          list: [
            "Commercial operating model",
            "Roles and decision rights",
            "Sales–marketing alignment",
            "Cross-functional ways of working",
            "Commercial planning",
            "Performance-management systems",
            "Commercial KPIs",
            "Management dashboards",
            "Review cadence",
            "Commercial analytics",
            "Revenue operations",
            "Forecasting discipline",
            "Decision support",
            "Tools and enablement",
            "Capability building",
            "Transformation governance",
            "Initiative tracking",
            "Continuous improvement",
          ],
          closing:
            "The objective is to move commercial performance from individual effort and periodic initiatives to an institutional capability embedded into how the business operates.",
        },
      ],
    },
  ],

  connectLead: "Seven capabilities. One commercial growth system.",
  connectIntro: "Commercial decisions should not be made independently.",
  connectStages: [
    { name: "Growth Strategy", desc: "defines where to grow." },
    { name: "Product Portfolio Strategy", desc: "determines what to sell." },
    { name: "Pricing & Revenue Management", desc: "determines how to capture value." },
    { name: "Go-To-Market & Market Entry", desc: "defines how to take the opportunity to market." },
    { name: "Channel & Route-to-Market", desc: "determines how customers will be reached and served." },
    { name: "Sales Transformation", desc: "builds the engine that wins and grows customers." },
    { name: "Commercial Excellence & Operating Model", desc: "makes performance repeatable." },
  ],
  connectClosing:
    "The commercial system is only as strong as the connections between these decisions.",

  approachLead: "From strategic choice to measurable commercial performance.",
  approach: [
    {
      title: "Discover",
      body: "We begin with the evidence. We understand:",
      list: [
        "Market",
        "Customers",
        "Competition",
        "Portfolio",
        "Pricing",
        "Channels",
        "Sales performance",
        "Economics",
        "Capabilities",
        "Organisation",
      ],
      after:
        "Where deeper evidence is required, our Market & Customer Intelligence practice provides the research and analysis needed to replace assumptions with market truth.",
    },
    {
      title: "Strategize",
      body: "We identify the opportunities and make the critical choices:",
      list: [
        "Where to play",
        "Which customers to prioritise",
        "Which products deserve investment",
        "How to price",
        "Which channels matter",
        "What commercial model can win",
      ],
      after: "Strategy should make choices — not produce a longer list of initiatives.",
    },
    {
      title: "Design",
      body: "We translate those choices into the commercial system required to execute them. That may include:",
      list: [
        "Portfolio architecture",
        "Pricing systems",
        "GTM model",
        "Route-to-market",
        "Sales structure",
        "Processes",
        "Incentives",
        "KPIs",
        "Governance",
      ],
    },
    {
      title: "Activate",
      body: "We work alongside management and commercial teams to move the strategy from presentation to market. Depending on the engagement, this can include implementation planning, sales activation, channel rollout, capability building and execution support.",
    },
    {
      title: "Optimize",
      body: "We measure the business outcomes, identify what is working and continuously improve the commercial system.",
    },
  ],
  approachClosing: "Discover → Strategize → Design → Activate → Optimize",

  deliverLead: "Not recommendations in isolation. A system the organisation can execute.",
  deliverIntro: "Depending on the engagement, deliverables may include:",
  deliverGroups: [
    {
      label: "Strategic Choices",
      items: [
        "Growth diagnostic",
        "Growth strategy",
        "Growth-pool analysis",
        "Strategic priorities",
        "Portfolio strategy",
        "Category strategy",
        "SKU rationalisation",
        "Pricing strategy",
        "Revenue-management framework",
      ],
    },
    {
      label: "Go-To-Market",
      items: [
        "Market-entry strategy",
        "GTM model",
        "Customer prioritisation",
        "Proposition framework",
        "Launch roadmap",
        "Channel strategy",
        "Route-to-market design",
        "Distributor/dealer model",
      ],
    },
    {
      label: "Sales Transformation",
      items: [
        "Sales organisation",
        "Territory and coverage model",
        "Key-account framework",
        "Sales process",
        "Pipeline architecture",
        "Incentive structure",
        "Sales playbooks",
        "Capability plans",
      ],
    },
    {
      label: "Commercial Excellence",
      items: [
        "Commercial operating model",
        "Roles and decision rights",
        "KPI architecture",
        "Performance dashboards",
        "Management routines",
        "Governance model",
        "Commercial analytics framework",
        "Transformation roadmap",
      ],
    },
  ],
  deliverClosing:
    "The exact deliverables follow the business problem — not a predetermined consulting template.",

  outcomesLead: "Commercial transformation should ultimately show up in the P&L.",
  outcomesIntro: "Depending on the challenge, our work is designed to help create:",
  outcomes: [
    "Higher revenue growth",
    "Improved gross margins",
    "Better EBITDA",
    "Increased market share",
    "Stronger product mix",
    "Better price realisation",
    "Improved promotional ROI",
    "Faster market entry",
    "Higher channel productivity",
    "Greater market coverage",
    "Improved distributor economics",
    "Higher sales productivity",
    "Better pipeline conversion",
    "Greater revenue per salesperson",
    "Reduced commercial complexity",
    "Stronger forecasting",
    "Better commercial decision-making",
    "More scalable growth",
  ],
  outcomesClosing:
    "We measure transformation through business outcomes — not activity alone.",

  impactLead: "Commercial transformation experience across strategy and execution.",
  impact: [
    {
      metric: "5× Sales Growth",
      body: "Helped build and scale a differentiated consumer business to a ₹150Cr+ annual run-rate, while reaching a Top 3 bestseller position on Amazon and improving marketing ROI by 28%.",
    },
    {
      metric: "+66% Sales Growth | 9-Point EBITDA Improvement",
      body: "Rebuilt the commercial economics of an e-commerce business, increasing sales by 66% while moving EBITDA from -6% to +3%.",
    },
    {
      metric: "First Profit in a Decade",
      body: "Led the turnaround of a consumer-durables category, taking the business to profitability for the first time since inception.",
    },
    {
      metric: "4,000+ Locations",
      body: "Led large-scale transformation across a corporate and franchise network spanning more than 4,000 locations internationally.",
    },
  ],
  impactNote:
    "Selected outcomes delivered by BLEZZINGS leadership across prior operating and consulting roles. They represent leadership experience and are not presented as BLEZZINGS client engagements.",

  whenLead: "Bring us in when the growth ambition is clear — but the path is not.",
  whenIntro:
    "Strategy & Commercial Transformation engagements are particularly relevant when:",
  whenBullets: [
    "Growth has slowed or plateaued",
    "Revenue is growing but margins are weakening",
    "Management is building its next three-to-five-year growth strategy",
    "You need to identify the next growth engine",
    "The product portfolio has become too complex",
    "New products repeatedly fail to scale",
    "Pricing lacks discipline",
    "Discounts and promotions are eroding margin",
    "You are entering a new market, category or geography",
    "Your distribution model needs redesign",
    "Channel productivity is weak",
    "Sales headcount is growing faster than sales",
    "Sales performance varies significantly across territories",
    "Commercial teams operate in silos",
    "Strategy is not translating into execution",
    "The organisation needs a more disciplined commercial operating system",
    "A business, category or channel needs to be turned around",
  ],
  whenAudience:
    "We typically work with CEOs, founders, business heads, chief commercial officers, chief sales officers, CMOs, strategy leaders, category heads and P&L owners.",

  whyLead:
    "Strategy without execution is a recommendation. Execution without strategy is activity.",
  whyIntro: ["Businesses have traditionally been forced to choose between:"],
  whyChoiceA: "Strategy firms that define what should happen",
  whyChoiceB: "Execution partners that implement individual pieces.",
  whyBridge: "BLEZZINGS was designed to bridge that gap.",
  whyBlocks: [
    {
      name: "P&L Thinking",
      paras: [
        "We evaluate growth through revenue, margins, customer economics and profitability — not vanity metrics.",
      ],
    },
    {
      name: "Market-Back, Not Inside-Out",
      paras: [
        "Our Market & Customer Intelligence capability grounds strategic choices in customers, markets and competitors rather than internal assumptions.",
      ],
    },
    {
      name: "The Whole Commercial System",
      paras: ["We can connect:"],
      highlight:
        "Growth → Portfolio → Pricing → GTM → Channels → Sales → Marketing → Digital",
      after: "rather than optimise one lever while ignoring the others.",
    },
    {
      name: "Strategy Through Execution",
      paras: [
        "Where appropriate, the same thinking can continue from strategic design into activation, capability building and performance improvement.",
      ],
    },
    {
      name: "Operator Perspective",
      paras: [
        "Our approach is shaped by experience managing businesses and commercial outcomes, not only advising on them.",
      ],
    },
  ],
  whyClosing: "One partner. From growth strategy to commercial performance.",

  relatedLead:
    "Commercial transformation becomes stronger when intelligence, brand and digital execution connect to it.",
  related: [
    {
      title: "Market & Customer Intelligence",
      body: "Understand markets, customers, competitors, segments and growth opportunities before making strategic choices.",
      to: "/capabilities/market-customer-intelligence",
    },
    {
      title: "Brand, Marketing & Communications",
      body: "Translate commercial strategy into positioning, propositions, communications and demand.",
      to: "/capabilities/brand-marketing-communications",
    },
    {
      title: "Digital & Commerce",
      body: "Turn commercial strategy into digital acquisition, marketplaces, conversion, retention and customer lifetime value.",
      to: "/capabilities/digital-commerce",
    },
  ],

  faqs: [
    {
      q: "What is commercial transformation?",
      a: "Commercial transformation is the systematic improvement of how a business creates, captures and grows revenue. It can involve changes to growth strategy, product portfolio, pricing, go-to-market, channels, sales organisation, commercial processes, capabilities and performance management.",
    },
    {
      q: "How is commercial transformation different from growth strategy?",
      a: "Growth strategy determines where and how the business should grow. Commercial transformation builds or changes the capabilities and operating system required to deliver that growth. The two are closely connected but solve different parts of the problem.",
    },
    {
      q: "What does a growth strategy engagement include?",
      a: "Depending on the business question, it may include growth diagnostics, market and customer opportunities, category or geographic expansion, adjacencies, growth pools, new growth engines, strategic choices and a prioritised value-creation roadmap.",
    },
    {
      q: "What is product portfolio strategy?",
      a: "Product portfolio strategy determines which categories, products and SKUs a business should grow, maintain, reposition, launch, consolidate or exit — and how investment should be allocated across the portfolio.",
    },
    {
      q: "What is revenue management?",
      a: "Revenue management systematically improves how a business captures value through pricing, pack architecture, promotions, discounts, trade investment and other commercial levers.",
    },
    {
      q: "What is the difference between Go-To-Market and Route-to-Market?",
      a: "Go-To-Market is the broader commercial strategy for winning a market or opportunity. It can include customers, proposition, pricing, channels, sales motion and demand generation. Route-to-Market focuses specifically on how products or services reach customers through direct sales, distributors, dealers, retail, marketplaces or other channels.",
    },
    {
      q: "What does Sales Transformation include?",
      a: "Sales Transformation can include sales strategy, organisation design, customer and account coverage, territories, sales processes, pipeline management, CRM, incentives, key-account management, forecasting, capability building and performance management.",
    },
    {
      q: "What is Commercial Excellence?",
      a: "Commercial Excellence builds the processes, capabilities, analytics, governance and management disciplines required to make strong commercial performance repeatable. It helps ensure improvements survive beyond individual initiatives or individual leaders.",
    },
    {
      q: "Does BLEZZINGS only develop strategy?",
      a: "No. Depending on the engagement, BLEZZINGS can continue beyond strategy into design, implementation planning, capability building and execution support. Our broader capabilities in brand, marketing, digital and commerce also allow commercial choices to flow into market activation.",
    },
    {
      q: "Do you work with both B2B and B2C businesses?",
      a: "Yes. The commercial models differ, but questions around growth, portfolio, pricing, customer coverage, channels, sales effectiveness and commercial operating models apply across both B2B and B2C organisations.",
    },
    {
      q: "Can BLEZZINGS support international market entry?",
      a: "Yes. Market-entry work can include market attractiveness, customer and competitor intelligence, proposition, pricing, route-to-market, distributors or partners, sales model, launch planning and GTM execution.",
    },
    {
      q: "How do you measure the success of a commercial-transformation programme?",
      a: "The measures depend on the problem but may include revenue growth, gross margin, EBITDA, market share, price realisation, product mix, channel productivity, sales productivity, pipeline conversion, distribution reach, customer acquisition and commercial return on investment. We establish measurable commercial outcomes at the beginning of the engagement.",
    },
  ],

  ctaHeading: "Your next phase of growth needs more than a strategy deck.",
  ctaBody:
    "Whether the challenge is finding the next growth engine, fixing portfolio complexity, improving pricing, entering a new market or transforming sales performance, BLEZZINGS can connect the strategic choices with the commercial system required to make them happen.",
  ctaLabel: "Discuss Your Growth Challenge",
};
